import { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { useParams } from 'react-router-dom'
import remarkGfm from 'remark-gfm'

import { Footer } from '@/components/footer'
import { Button } from '@/components/ui/button'
import {
  blogArticlePath,
  blogListPath,
  blogSources,
  resolveBlogSource,
  type BlogContentLanguage,
  type BlogMetadata,
} from '@/content/blogs'
import { useI18n } from '@/lib/i18n'
import { cn, formatDate } from '@/lib/utils'
import {
  applyContentPageHead,
  contentLanguageTag,
  currentContentLinkContext,
  localizeContentHref,
} from '@/pages/content-link-utils'

type ArticleLoadState
  = | { key: string, status: 'loading' }
    | { key: string, status: 'ready', metadata: BlogMetadata, article: string }
    | { key: string, status: 'error' }

export function BlogArticlePage() {
  const { blogId, legacyLocale } = useParams<{
    blogId: string
    legacyLocale?: string
  }>()
  const { language, t, isLoading: translationsLoading } = useI18n()

  const resolvedSource = useMemo(() => {
    const source = blogSources.find(entry => entry.id === blogId)
    if (!source || (legacyLocale && legacyLocale !== 'en' && legacyLocale !== 'zh'))
      return null

    const preferredLanguage = (legacyLocale as BlogContentLanguage | undefined) ?? language
    const variant = resolveBlogSource(source, preferredLanguage)
    return variant ? { ...variant, source } : null
  }, [blogId, legacyLocale, language])

  const sourceKey = resolvedSource
    ? `${resolvedSource.id}:${resolvedSource.locale}`
    : null
  const [hasServerPrelude] = useState(() =>
    typeof document !== 'undefined'
    && document.getElementById('seo-blog-article-prelude') !== null)
  const [loadState, setLoadState] = useState<ArticleLoadState | null>(null)
  const currentLoad = loadState?.key === sourceKey ? loadState : null
  const isLoading = Boolean(resolvedSource && (!currentLoad || currentLoad.status === 'loading'))
  const errorKey = !resolvedSource
    ? 'notFound'
    : currentLoad?.status === 'error' ? 'loadFailed' : null
  const metadata = currentLoad?.status === 'ready' ? currentLoad.metadata : null
  const article = currentLoad?.status === 'ready' ? currentLoad.article : null
  const firstHeading = article?.match(/^\s*#[ \t]+(\S[^\r\n]*)(?:\r?\n|$)/)
  const displayedHeading = firstHeading?.[1] ?? metadata?.title
  const articleBody = firstHeading && article
    ? article.slice(firstHeading[0].length)
    : article

  useEffect(() => {
    if (!resolvedSource || !sourceKey)
      return

    let isMounted = true
    setLoadState({ key: sourceKey, status: 'loading' })

    const loadArticle = async () => {
      try {
        const [metadataResponse, articleResponse] = await Promise.all([
          fetch(`${resolvedSource.basePath}/metadata.json`),
          fetch(`${resolvedSource.basePath}/content.md`),
        ])
        if (!metadataResponse.ok || !articleResponse.ok)
          throw new Error(`Unable to load article at ${resolvedSource.basePath}`)

        const [parsedMetadata, text] = await Promise.all([
          metadataResponse.json() as Promise<BlogMetadata>,
          articleResponse.text(),
        ])
        if (isMounted) {
          setLoadState({
            key: sourceKey,
            status: 'ready',
            metadata: parsedMetadata,
            article: text,
          })
        }
      }
      catch (fetchError) {
        console.error(fetchError)
        if (isMounted)
          setLoadState({ key: sourceKey, status: 'error' })
      }
    }

    void loadArticle()
    return () => {
      isMounted = false
    }
  }, [resolvedSource, sourceKey])

  useLayoutEffect(() => {
    if (translationsLoading || isLoading)
      return

    if (!resolvedSource || !metadata || article === null) {
      if (document.getElementById('seo-blog-article-prelude'))
        return
      return applyContentPageHead({
        title: t('blog.article.notFoundTitle'),
        description: t('blog.article.notFoundDescription'),
        htmlLanguage: contentLanguageTag(language),
        interfaceLanguage: language,
        robots: 'noindex,nofollow',
      })
    }

    const canonicalUrl = `https://mentiss.ai${resolvedSource.path}`
    const htmlLanguage = contentLanguageTag(language)
    const imageUrl = metadata.coverImage
      ? new URL(metadata.coverImage, 'https://mentiss.ai').toString()
      : undefined
    const alternates = resolvedSource.source.locales.zh
      ? (['en', 'zh'] as const).map(locale => ({
          hreflang: contentLanguageTag(locale),
          href: `https://mentiss.ai${blogArticlePath(resolvedSource.id, locale)}`,
        }))
      : []
    if (alternates.length > 0) {
      alternates.push({
        hreflang: 'x-default',
        href: `https://mentiss.ai${blogArticlePath(resolvedSource.id, 'en')}`,
      })
    }

    const cleanup = applyContentPageHead({
      title: metadata.title,
      description: metadata.description,
      htmlLanguage,
      interfaceLanguage: language,
      robots: language === resolvedSource.locale ? 'index,follow' : 'noindex,follow',
      canonicalUrl,
      alternates,
      imageUrl,
      pageType: 'article',
      structuredDataId: 'blog-structured-data',
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        'headline': metadata.title,
        'description': metadata.description,
        'url': canonicalUrl,
        'mainEntityOfPage': canonicalUrl,
        'inLanguage': contentLanguageTag(resolvedSource.locale),
        'datePublished': metadata.publishedAt,
        'dateModified': metadata.publishedAt,
        'image': imageUrl,
        'author': { '@type': 'Organization', 'name': 'Mentiss' },
        'publisher': {
          '@type': 'Organization',
          'name': 'Mentiss',
          'logo': { '@type': 'ImageObject', 'url': 'https://mentiss.ai/asset/logo.jpg' },
        },
      },
    })
    document.getElementById('seo-blog-article-prelude')?.remove()
    return cleanup
  }, [article, isLoading, language, metadata, resolvedSource, t, translationsLoading])

  const pageTitle = metadata?.title ?? t('blog.article.notFoundTitle')
  const pageDescription = metadata?.description ?? t('blog.article.notFoundDescription')
  const backPath = blogListPath(language)
  const contentLinkContext = {
    ...currentContentLinkContext(),
    contentLanguage: resolvedSource?.locale,
  }

  if (hasServerPrelude && (isLoading || errorKey || translationsLoading))
    return null

  return (
    <div className="min-h-screen bg-background text-foreground">
      {resolvedSource && metadata ? (
        <>
          <header className="border-b border-border bg-background/95 pt-24 backdrop-blur">
            <div className="mx-auto flex max-w-5xl flex-col gap-6 px-4 pb-10">
              <Button asChild variant="ghost" className="w-fit">
                <a href={backPath}>{t('blog.article.backLink')}</a>
              </Button>
              <div className="flex flex-col gap-3">
                <p className="text-sm uppercase tracking-wide text-muted-foreground">
                  {formatDate(metadata.publishedAt)}
                </p>
                <h1 className="text-3xl font-bold md:text-4xl" lang={contentLanguageTag(resolvedSource.locale)}>{displayedHeading}</h1>
                <p className="text-base text-muted-foreground md:text-lg" lang={contentLanguageTag(resolvedSource.locale)}>{metadata.description}</p>
              </div>
            </div>
          </header>

          {metadata.coverImage ? (
            <div className="border-b border-border">
              <img
                src={metadata.coverImage}
                alt={metadata.title}
                className="h-80 w-full object-cover"
                loading="lazy"
              />
            </div>
          ) : null}
        </>
      ) : null}

      <main className="mx-auto flex max-w-3xl flex-col gap-6 px-4 py-10 md:px-0">
        {isLoading ? (
          <p className="text-muted-foreground">{t('blog.article.loading')}</p>
        ) : errorKey ? (
          <div className="flex flex-col gap-4">
            <h1 className="text-3xl font-semibold">{pageTitle}</h1>
            <p className="text-muted-foreground">{pageDescription}</p>
            <p className="text-destructive">
              {errorKey === 'notFound'
                ? t('blog.article.notFoundMessage')
                : t('blog.article.loadFailedMessage')}
            </p>
            <Button asChild variant="ghost" className="w-fit">
              <a href={backPath}>{t('blog.article.backLink')}</a>
            </Button>
          </div>
        ) : articleBody !== null ? (
          <article
            lang={resolvedSource ? contentLanguageTag(resolvedSource.locale) : undefined}
            className={cn(
              'prose prose-neutral dark:prose-invert',
              'max-w-none',
            )}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ node, ...props }) => {
                  void node
                  const href = localizeContentHref(props.href, contentLinkContext)
                  const isExternal = href?.startsWith('http')
                  return (
                    <a
                      {...props}
                      href={href}
                      target={isExternal ? '_blank' : undefined}
                      rel={isExternal ? 'noopener noreferrer' : undefined}
                    />
                  )
                },
              }}
            >
              {articleBody}
            </ReactMarkdown>
          </article>
        ) : null}
      </main>

      <Footer />
    </div>
  )
}
